/**
 * Easter bookmarks — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterBookmarksPack: ToilePack = {
    slug: 'easter-bookmarks',
    category: 'season',
    name: 'Easter bookmarks',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bunny', label: 'Bunny', subject: 'A sheet of four tall bookmarks each with a bunny motif and a tassel hole at the top, the foot of each lettered exactly "Hop to it"'},
        {key: 'tulips', label: 'Tulips', subject: 'A sheet of four tall bookmarks each with a row of tulips running the length and a tassel hole, the foot of each lettered exactly "Spring is here"'},
        {key: 'greeting', label: 'Greeting', subject: 'A sheet of four tall bookmarks each lettered "Happy Easter" down the length with eggs, a tassel hole, dashed cut lines'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel', style: 'pastel easter printable, egg and floral borders, soft rounded lettering, print-ready', palette: 'Soft pink, mint, lilac and butter yellow', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'vintage', label: 'Vintage', style: 'vintage easter printable, engraved floral borders, elegant serif lettering, print-ready', palette: 'Rose, sage, cream and a little gold', framing: 'Laid out on a cream page with an ornate border and a margin'},
        {key: 'kids', label: 'Kids', style: "kids' easter printable, bright cartoon bunnies and chicks, rounded lettering, print-ready", palette: 'Bright yellow, sky blue, grass green and pink', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['with a chick instead of a bunny', 'for a Sunday school class set', 'in a longer, narrower shape'],
    directionHint: 'try: a chick instead of a bunny',
    seo: {
        title: 'Easter Bookmarks: Bunny, Tulip & Greeting Designs',
        metaDescription: "Nine Easter bookmarks — a bunny set, a tulip set, and a lettered greeting set, four to a sheet — in pastel, vintage, and kids' styles, print-ready.",
        h1: 'Easter bookmarks: bunny, tulip, and greeting designs',
        tagline: 'Nine sheets, three bookmark designs, three styles each — four bookmarks per sheet, cut lines drawn.',
        intro: "Three Easter bookmark sheets — a bunny design, a tulip row, and a lettered greeting — each printing four tall bookmarks per page with dashed cut lines, in pastel, vintage, and kids' cartoon styles. Ask for a different motif, a tassel-hole size change, or a name line, and the same bookmark grammar carries it.",
        useCases: [
            'a classroom Easter treat',
            'a library spring display',
            'a Sunday school gift',
            'an egg-hunt party favor',
        ],
        faq: [
            {q: 'How many bookmarks are on one sheet?', a: 'Four, arranged in a column with dashed cut lines between them — cut along the lines after printing.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single vertical page, close to US letter or A4 proportions.'},
            {q: 'Can I get a different motif or add a name line?', a: 'Yes — the direction field takes any variation, including a different motif or a blank name line.'},
            {q: 'What are the three styles?', a: "Pastel, vintage engraved-border, and bright kids' cartoon."},
        ],
    },
}
